import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import useQueryClient from "../../api/query";
import { COLORS, TYPOGRAPHY } from "./constants";
import ManagerReviewSection from "./ManagerReviewSection";
import MetricCardsSection from "./MetricCardsSection";
import type {
  ManagerReviewItem,
  MetricCardData,
  NotificationWithTemplate,
} from "./types";

interface CopilotProps {
  selectedPractices: PracticeMessage[];
  fullname: string;
}

const getTimeBasedGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
};

const getFirstName = (fullname: string): string =>
  fullname.split(" ")[0] || fullname;

export default function Copilot({ selectedPractices, fullname }: CopilotProps) {
  const greeting = getTimeBasedGreeting();
  const firstName = getFirstName(fullname);
  const [metrics, setMetrics] = useState<MetricCardData[]>([]);
  const [reviewItems, setReviewItems] = useState<ManagerReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const practiceIdsString = React.useMemo(
    () =>
      JSON.stringify(
        selectedPractices.map((practice) => practice.wieldyId).sort(),
      ),
    [selectedPractices],
  );

  const practiceIds = React.useMemo(
    () => JSON.parse(practiceIdsString) as string[],
    [practiceIdsString],
  );

  const loadNotifications = React.useCallback(async () => {
    const notificationsData = await queryClient.getNotifications(practiceIds);

    const mappedNotifications: ManagerReviewItem[] = notificationsData.map(
      (notification: NotificationWithTemplate) => {
        const agentName = notification.agent.toLowerCase();
        const iconUrl =
          notification.agentIconUrl ||
          `/static/images/agent-icons/${agentName}.svg`;

        return {
          id: notification.id,
          iconUrl,
          heading: notification.title,
          description: notification.messageTemplate,
          count: notification.config?.count || 0,
          link: notification.config?.action?.url,
          agent: notification.agent,
          templateCode: notification.templateCode,
          practiceId: notification.practiceId,
        };
      },
    );
    setReviewItems(mappedNotifications);
  }, [queryClient, practiceIds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding loadNotifications, queryClient.getAgentMetrics, practiceIds or dropping practiceIdsString would change when this runs
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const metricsData = await queryClient.getAgentMetrics(practiceIds);
        setMetrics(metricsData.cards);

        await loadNotifications();
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to load Copilot dashboard data:", err);
        setError("Failed to load your daily brief. Try again shortly.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [practiceIdsString]);

  if (loading || error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <Typography
            sx={{ ...TYPOGRAPHY.body.medium, color: COLORS.text.secondary }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: COLORS.background.main,
        minHeight: "calc(100vh - 200px)",
        paddingTop: 4,
        paddingBottom: 4,
        width: "100%",
      }}
    >
      <Box sx={{ mb: 4, px: 4 }}>
        <Typography
          variant="h4"
          sx={{
            ...TYPOGRAPHY.heading.h4,
            mb: 0.5,
          }}
        >
          {greeting}, {firstName}!
        </Typography>
        <Typography
          variant="body2"
          sx={{
            ...TYPOGRAPHY.body.medium,
            color: COLORS.text.secondary,
          }}
        >
          Here&apos;s your daily brief from your AI Agents
        </Typography>
      </Box>

      <MetricCardsSection metrics={metrics} />

      <Box sx={{ px: 4 }}>
        <ManagerReviewSection
          reviewItems={reviewItems}
          queryClient={queryClient}
          practiceIds={practiceIds}
          onReload={loadNotifications}
        />
      </Box>
    </Box>
  );
}
